import { GameState } from "../state";
import { resolveWeapon, type ResolvedWeapon } from "../stats";
import { len } from "../../core/math";
import type { Enemy, Projectile } from "../entities";

// Fires each equipped weapon on its own cooldown. Instant-hit behaviors (zap,
// chain, beam) resolve immediately with a Beam visual; projectile behaviors
// push Projectile entities that the projectile system advances.
export function updateWeapons(s: GameState, dt: number): void {
  for (const w of s.weapons) {
    w.timer -= dt;
    if (w.timer > 0) continue;
    const r = resolveWeapon(w.def, s.stats);
    w.timer = r.cooldown;
    fire(s, w.def.behavior, w.def.color, r);
    s.firedThisFrame = true; // signal the shoot SFX (throttled in the audio engine)
  }
}

function rollCrit(s: GameState, r: ResolvedWeapon, base: number): { dmg: number; crit: boolean } {
  const crit = s.rng.chance(r.critChance);
  return { dmg: crit ? base * r.critMult : base, crit };
}

function fire(s: GameState, behavior: string, color: string, r: ResolvedWeapon): void {
  const p = s.player;
  switch (behavior) {
    case "zap": {
      const targets = nearestN(s, p.x, p.y, r.range, Math.max(1, r.count));
      for (const e of targets) {
        const { dmg, crit } = rollCrit(s, r, r.damage);
        const k = kb(p.x, p.y, e, r.knockback);
        s.addBeam(p.x, p.y, e.x, e.y, color, crit ? 4 : 2.5);
        s.damageEnemy(e, dmg, crit, k.x, k.y);
      }
      break;
    }
    case "chain": {
      // Lightning that jumps to successive nearest unhit enemies.
      const hit = new Set<Enemy>();
      let fx = p.x;
      let fy = p.y;
      const jumps = Math.max(1, r.count);
      for (let i = 0; i < jumps; i++) {
        const e = s.nearestEnemy(fx, fy, r.range, hit);
        if (!e) break;
        hit.add(e);
        const { dmg, crit } = rollCrit(s, r, r.damage);
        s.addBeam(fx, fy, e.x, e.y, color, crit ? 3.5 : 2);
        s.damageEnemy(e, dmg, crit, 0, 0);
        fx = e.x;
        fy = e.y;
      }
      break;
    }
    case "beam": {
      // Aim at nearest; resolve as an instant piercing line.
      const target = s.nearestEnemy(p.x, p.y, r.range);
      if (!target) break;
      const a = Math.atan2(target.y - p.y, target.x - p.x);
      p.facing = a;
      const ex = p.x + Math.cos(a) * r.range;
      const ey = p.y + Math.sin(a) * r.range;
      s.addBeam(p.x, p.y, ex, ey, color, 4, 0.16);
      let pierced = 0;
      for (const e of enemiesAlongLine(s, p.x, p.y, a, r.range)) {
        const { dmg, crit } = rollCrit(s, r, r.damage);
        const k = kb(p.x, p.y, e, r.knockback);
        s.damageEnemy(e, dmg, crit, k.x, k.y);
        if (++pierced > r.pierce) break;
      }
      break;
    }
    case "burst": {
      const target = s.nearestEnemy(p.x, p.y, r.range);
      const baseA = target ? Math.atan2(target.y - p.y, target.x - p.x) : p.facing;
      const n = Math.max(1, r.count);
      for (let i = 0; i < n; i++) {
        const t = n === 1 ? 0 : i / (n - 1) - 0.5;
        spawnProjectile(s, p.x, p.y, baseA + t * 0.7, color, r);
      }
      break;
    }
    case "projectile":
    default: {
      const target = s.nearestEnemy(p.x, p.y, r.range);
      if (!target) break;
      const a = Math.atan2(target.y - p.y, target.x - p.x);
      p.facing = a;
      const n = Math.max(1, r.count);
      for (let i = 0; i < n; i++) {
        const spread = n === 1 ? 0 : (i - (n - 1) / 2) * 0.12;
        spawnProjectile(s, p.x, p.y, a + spread, color, r);
      }
      break;
    }
  }
}

// Manual fire (Space): launch a bolt at the nearest target WITHIN a bounded
// reach, so you can't snipe the whole board from the middle — you must move
// toward distant targets. Scales with the player's damage/projectile/crit stats.
const MANUAL_BASE_DMG = 13;
export const MANUAL_RANGE = 250;

export function fireManualShot(s: GameState): void {
  const p = s.player;
  // Auto-aim only at enemies inside the reach; otherwise fire where we face.
  const target = s.nearestEnemy(p.x, p.y, MANUAL_RANGE);
  const angle = target ? Math.atan2(target.y - p.y, target.x - p.x) : p.facing;
  p.facing = angle;

  const critChance = 0.1 + s.stats.critChanceAdd;
  const crit = s.rng.chance(critChance);
  const damage = MANUAL_BASE_DMG * s.stats.damageMult * (crit ? 2.2 : 1);
  const speed = 1250 * s.stats.projectileSpeedMult;
  // Bound travel distance to EXACTLY the reach (no +fudge that grows with speed).
  const life = MANUAL_RANGE / speed;

  s.projectiles.push({
    alive: true,
    x: p.x,
    y: p.y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    radius: 6,
    damage,
    pierce: 1 + s.stats.pierceAdd,
    life,
    color: "#A6E9F0",
    crit,
    knockback: 30 * s.stats.knockbackMult,
    hitSet: new Set(),
  });
  s.spawnParticles(p.x + Math.cos(angle) * p.radius, p.y + Math.sin(angle) * p.radius, "#A6E9F0", 2, 120);
  s.firedThisFrame = true; // signal the shoot SFX
}

function spawnProjectile(s: GameState, x: number, y: number, angle: number, color: string, r: ResolvedWeapon): void {
  const { dmg, crit } = rollCrit(s, r, r.damage);
  const proj: Projectile = {
    alive: true,
    x,
    y,
    vx: Math.cos(angle) * r.projectileSpeed,
    vy: Math.sin(angle) * r.projectileSpeed,
    radius: 6,
    damage: dmg,
    pierce: r.pierce,
    life: 2.2,
    color,
    crit,
    knockback: r.knockback,
    hitSet: new Set(),
  };
  s.projectiles.push(proj);
}

function nearestN(s: GameState, x: number, y: number, range: number, n: number): Enemy[] {
  const out: Enemy[] = [];
  const used = new Set<Enemy>();
  for (let i = 0; i < n; i++) {
    const e = s.nearestEnemy(x, y, range, used);
    if (!e) break;
    used.add(e);
    out.push(e);
  }
  return out;
}

function enemiesAlongLine(s: GameState, x: number, y: number, angle: number, range: number): Enemy[] {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const hits: { e: Enemy; t: number }[] = [];
  for (const e of s.enemies) {
    if (!e.alive) continue;
    const rx = e.x - x;
    const ry = e.y - y;
    const t = rx * dx + ry * dy; // projection along the ray
    if (t < 0 || t > range) continue;
    const perp = Math.abs(rx * dy - ry * dx);
    if (perp <= e.radius + 8) hits.push({ e, t });
  }
  hits.sort((a, b) => a.t - b.t);
  return hits.map((h) => h.e);
}

function kb(fromX: number, fromY: number, e: Enemy, amount: number): { x: number; y: number } {
  if (amount <= 0) return { x: 0, y: 0 };
  const dx = e.x - fromX;
  const dy = e.y - fromY;
  const m = len(dx, dy) || 1;
  return { x: (dx / m) * amount, y: (dy / m) * amount };
}
